import { useContext, useRef } from "react";
import {
    AbortError,
    createFailure,
    ensure,
    FailureSeverity,
    validationFailed,
    validationSuccessful
} from "sprint-common-web";

import { MovieContentSubtype } from "../../../../../common/types/MovieContentSubtype";
import { MovieReference } from "../../../api/GetMoviesBySimilarTitleResponse";
import { UpdateMovieDetailsRequest } from "../../../api/UpdateMovieDetailsRequest";
import { useAssetsManageMovieApi } from "../../../api/useAssetsManageMovieApi";
import { ValidateMovieTitleRequest } from "../../../api/ValidateMovieTitleRequest";
import { MovieContext } from "../../context/MovieContext";
import { EditMovieInfoFormInputs } from "./EditMovieInfoFormInputs";

export const useEditMovieInfoForm = (contentType: MovieContentSubtype) => {
    const { movie, setMovieDetails } = useContext(MovieContext);
    ensure(movie);

    const defaultInputs: EditMovieInfoFormInputs = {
        title: movie.title,
        year: movie.year ?? null
    };

    const abortController = useRef<AbortController>(new AbortController());

    const { updateMovieDetails, getMoviesBySimilarTitle } = useAssetsManageMovieApi();

    const updateMovie = async (inputs: EditMovieInfoFormInputs): Promise<void> => {
        await updateMovieDetails(movie.id, createRequest(inputs));
        setMovieDetails(inputs.title, inputs.year ?? undefined);
    };

    const getMoviesWithSimilarTitle = async (title: string): Promise<MovieReference[]> => {
        if (title) {
            const request: ValidateMovieTitleRequest = {
                title,
                id: movie.id,
                subtype: contentType
            };

            const response = await getMoviesBySimilarTitle(request, abortController.current.signal);

            return response.items;
        }

        return [];
    };

    const validateTitle = (input: string | undefined) => {
        abortController.current.abort();
        abortController.current = new AbortController();

        if (!input) {
            return validationSuccessful();
        }

        return getMoviesWithSimilarTitle(input)
            .then((movies: MovieReference[]) => {
                if (!movies || movies.length < 1) return validationSuccessful();

                const warningMessage = `Similar titles: ${movies.map((item: MovieReference) => item.title)}`;
                const failure = createFailure(warningMessage, "movie.SimilarTitle", FailureSeverity.Warning);

                return validationFailed([failure]);
            })
            .catch((error: Error) => {
                if (error instanceof AbortError) {
                    return validationSuccessful();
                }

                throw error;
            });
    };

    return {
        defaultInputs,
        updateMovie,
        validateTitle
    };
};

const createRequest = (inputs: EditMovieInfoFormInputs): UpdateMovieDetailsRequest => {
    return { title: inputs.title, year: inputs.year ?? undefined };
};
